import { Link, Navigate, useMatches } from "@tanstack/react-router";
import { Inbox, Lock, Sparkles } from "lucide-react";
import type { ReactNode } from "react";

import { EMPLOYEE_HOME, HOME_PATH, pageRefusal } from "@/app/registry";
import { useProPrice } from "@/app/proPrice";
import { useSession } from "@/app/session";
import { brand } from "@/brand";
import { buttonClass, Card, EmptyState } from "@/components/ui";

const NOTES = {
  admin: {
    title: "This page is for administrators",
    body: "It is part of the admin console, which only the platform's administrators can open.",
  },
  ai: {
    title: `${brand.name} AI is by invitation`,
    body: "The admin opens the AI paper trades to selected accounts. Ask the admin if you'd like access.",
  },
  ra: {
    title: "This is a Research Analyst's tool",
    body: "Posting calls and managing clients is for SEBI-registered Research Analysts on the platform.",
  },
  pro: {
    title: "Available to members",
    body: "Your account as your Research Analyst's client includes their calls, their profile and track record, and the basic market pages. Become a member for the screener, F&O analytics, option chain, commodities, backtests and the AI paper trades.",
  },
} as const;

/** For an RA's client on a research tool: what the account includes, and the upgrade. */
export function UpgradeCard() {
  const note = NOTES.pro;
  const price = useProPrice();
  return (
    <Card className="mx-auto max-w-lg">
      <EmptyState
        icon={Sparkles}
        title={note.title}
        className="py-12"
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Link
              to="/app/payment"
              search={{ plan: "investor_pro" } as never}
              className={buttonClass("primary", "md")}
            >
              {price ? `Upgrade (${price})` : "Become a member"}
            </Link>
            <Link to="/app/feed" className={buttonClass("secondary", "md")}>
              Go to My Feed
            </Link>
          </div>
        }
      >
        {note.body}
      </EmptyState>
    </Card>
  );
}

/** "Find an RA" for an RA's client: their analyst brought them here. */
function RaClientNote() {
  return (
    <Card className="mx-auto max-w-lg">
      <EmptyState
        icon={Inbox}
        title="You're here with your Research Analyst"
        className="py-12"
        action={
          <Link to="/app/feed" className={buttonClass("primary", "md")}>
            Go to My Feed
          </Link>
        }
      >
        Your Research Analyst brought you here, so there's no need to look for
        another one. Their calls, profile and track record are in My Feed.
      </EmptyState>
    </Card>
  );
}

/**
 * A page this account can't use (an admin page, AI research without an
 * invitation, an RA's own tools) shows a short note instead of the server's
 * refusal. The server enforces access on its own; this only explains it.
 */
export function RoleGate({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const matches = useMatches();
  const access = [...matches].reverse().find((m) => m.staticData?.access)
    ?.staticData.access;
  const reason = access
    ? pageRefusal(
        access,
        user?.role,
        user?.ai_calls_access === true,
        user?.access_tier ?? "full",
        user?.ra_client === true,
      )
    : null;
  if (!reason) return <>{children}</>;
  // employees work in the sales console: any other page takes them there
  if (reason === "employee") return <Navigate to={EMPLOYEE_HOME} replace />;
  // the route's beforeLoad already sent them Home (router.tsx); never render it
  if (reason === "hidden") return null;
  if (reason === "pro") return <UpgradeCard />;
  if (reason === "ra_client") return <RaClientNote />;
  const note = NOTES[reason];
  return (
    <Card className="mx-auto max-w-lg">
      <EmptyState
        icon={Lock}
        title={note.title}
        className="py-12"
        action={
          <Link to={HOME_PATH} className={buttonClass("secondary", "md")}>
            Go to Home
          </Link>
        }
      >
        {note.body}
      </EmptyState>
    </Card>
  );
}
